import React from 'react'
import { find, last, some, split } from 'lodash'
import { Element, scroller } from 'react-scroll'
import ReactTable from 'react-table'
import uniqid from 'uniqid'

import { RatedEntityCell, ratedEntityFilter } from './RatedEntityCell'
import RatingsHistoryPopup from './RatingsHistoryPopup'
import { RatingsCell, ratingsFilter } from './RatingsCell'
import { EntityDetailsCell, entityDetailsFilter } from './EntityDetailsCell'
import { IdentifiersCell, identifiersFilter } from './IdentifiersCell'
import { RatableDetailsCell, ratableDetailsFilter } from './RatableDetailsCell'
import { HeaderCell } from './HeaderCell'
import { RatablesFilter } from './RatablesFilter'
import { RatablesSort } from './RatablesSort'
import { RatablesRatingsFilter } from './RatablesRatingsFilter'
import { LocalizationContext } from '../utils'

export type RatablesMarketingItemProps = {
  name: string
  slug: string
}

export type RatablesAnalystProps = {
  firstName: string
  lastName: string
  type: string
}

export type RatablesMarketingProps = {
  analysts: RatablesAnalystProps[] | null
  countries: RatablesMarketingItemProps[]
  sectors: RatablesMarketingItemProps[]
}

export type RatablesRatingItemProps = {
  ratingId: string
  ratingEffectiveDate: Date
  ratingCode: string
  ratingAction: string
  ratingType: string
  recoveryRatingValue: string
  filterRatingType: string
  filterNationalRatingType: string
  filterInvestmentGradeType: string
  ratingAlertCode: string
}

export type RatablesRatingProps = {
  id?: string
  country?: string
  ratingActionDescription?: string
  ratingTypeDescription?: string
  ratingAlertCode?: string | null
  ratableName?: string
  ratingTypeId?: number
  ratingLocalActionDescription?: string
  ratingAlertDescription?: string
  ratingCode?: string
  ratingLocalValue?: string
  ratingEffectiveDate?: string
  originalDate?: string
  solicitFlag?: string | null
  countryId?: number
  latestFlag?: boolean
}

export type RatablesEndorsementProps = {
  agency: string
  status: string
  postBrexitRegAgency?: string
}

export type RatablesSolicitationProps = {
  ratingType: string
  solicitationType: string
}

export type EntityDisclosureProps = {
  originalRatings: RatablesRatingProps[]
  endorsements: RatablesEndorsementProps[]
  solicitations: RatablesSolicitationProps[]
  solicitationTypeDescription: string
}

export type RatablesIdentifierProps = {
  type: string
  value: string
}

export type RatablesEntityProps = {
  disclosure: EntityDisclosureProps
  groupManager: RatablesMarketingItemProps[]
  identifiers: RatablesIdentifierProps[]
  marketing: RatablesMarketingProps
  ratables: any[]
  ratablesList?: any[]
  transactionSecurityRatings?: any[]
}

export type RatablesProps = {
  entity: RatablesEntityProps
  handleURLParams: (ratableId: string) => void
  isUspf?: boolean
  ratablesRatingHistory?: any
  isTaggedToBrazil: boolean
  isTaggedToLatAm: boolean
  ratables: any[]
}

export enum RatablesFilterTypes {
  INTERNATIONAL = 'International',
  NATIONAL = 'National / Local',
  SHORT_TERM = 'Short Term',
  LONG_TERM = 'Long Term',
  SPECULATIVE_GRADE = 'Speculative Grade',
  INVESTMENT_GRADE = 'Investment Grade'
}

export enum RatablesSortByTypes {
  MOST_RECENT = 'mostRecent',
  DEBT_TYPE = 'debtType'
}

export enum ColumnIDs {
  RATED_ENTITY = 'ratedEntity',
  RATINGS = 'ratings',
  ENTITY_DETAILS = 'entityDetails',
  IDENTIFIERS = 'identifiers',
  RATABLE_DETAILS = 'ratableDetails'
}

interface RatablesState {
  data: any[]
  displayLocalValues: boolean
  expanded: any[]
  isOpen: boolean
  launchRatingHistoryPopup: boolean
  originalData: any[]
  page: number
  pageSize: number
  sorted: any[]
}

export class Ratables extends React.Component<RatablesProps, RatablesState> {
  static contextType = LocalizationContext

  state = {
    data: [],
    displayLocalValues: false,
    expanded: [],
    isOpen: false,
    launchRatingHistoryPopup: false,
    originalData: [],
    page: 0,
    pageSize: 10,
    sorted: []
  }

  componentDidMount() {
    const { entity, isUspf } = this.props
    const data = this.createData(entity)
    const originalData = [...data]
    const ratableIdHash = window.location.hash
      ? last(split(window.location.hash, '-'))
      : ''
    const isPresentRatableId = some(data, ['id', ratableIdHash])

    const hasIssuesHash = window.location.hash
      ? window.location.hash === '#issues'
      : false
    this.setState({ data, originalData })
    if (isUspf) {
      this.setState({ sorted: [{ id: ColumnIDs.RATED_ENTITY, desc: false }] })
    }
    if (isPresentRatableId) {
      this.setState({
        launchRatingHistoryPopup: true
      })
      scroller.scrollTo('ratables', {
        duration: 750,
        offset: -75,
        smooth: true,
        spy: true
      })
    } else if (hasIssuesHash) {
      scroller.scrollTo('ratables', {
        duration: 750,
        offset: -75,
        smooth: true,
        spy: true
      })
    }
  }

  // eslint-disable-next-line class-methods-use-this
  ratableTypeDesc = (debtTypeSubDebtTypeValue: any, item: any) => {
    let debtTypeSubVal = debtTypeSubDebtTypeValue
    if (debtTypeSubVal) {
      debtTypeSubVal = debtTypeSubVal
        .concat('; ')
        .concat(item.ratableTypeDescription)
    } else if (debtTypeSubVal === '') {
      debtTypeSubVal = debtTypeSubVal.concat(item.ratableTypeDescription)
    }
    return debtTypeSubVal
  }

  createData = (entity: RatablesEntityProps) => {
    const {
      disclosure = { endorsements: [], solicitationTypeDescription: '' },
      groupManager,
      marketing = {
        countries: [],
        analysts: []
      },
      transactionSecurityRatings
    } = entity

    const { isUspf } = this.props
    const { endorsements = [] } = disclosure || {}
    const { countries = [], analysts = [] } = marketing || {}
    const [country = {}] = countries || []
    const [manager = {}] = groupManager || []
    const { ratables = [] } = this.props

    const { language } = this.context

    return ratables.map((item: any) => {
      const { sectors = [] } = item.marketing || {}
      let debtTypeSubDebtTypeValue = ''
      if (!isUspf) {
        if (item.subgroupName) {
          debtTypeSubDebtTypeValue = debtTypeSubDebtTypeValue.concat(
            item.subgroupName
          )
        }

        if (item.ratableTypeDescription) {
          debtTypeSubDebtTypeValue = this.ratableTypeDesc(
            debtTypeSubDebtTypeValue,
            item
          )
        }
      }
      let { identifiers } = item
      let result

      if (identifiers && identifiers.length > 0)
        result = identifiers.filter(
          (identifier: any) => identifier.type === 'debtTypeSubDebtType'
        )

      if (result === undefined || result.length === 0) {
        if (identifiers && identifiers.length > 0 && debtTypeSubDebtTypeValue)
          identifiers.unshift({
            type: 'debtTypeSubDebtType',
            value: debtTypeSubDebtTypeValue
          })
        else if (debtTypeSubDebtTypeValue) {
          identifiers = [
            {
              type: 'debtTypeSubDebtType',
              value: debtTypeSubDebtTypeValue
            }
          ]
        }
      }

      // Set the transaction security ratings
      let longTermSecurityRating = ''
      let longTermSecurityRatingAlertCode = ''
      let shortTermSecurityRating = ''
      let shortTermSecurityRatingAlertCode = ''
      if (item.transactionSecurityID && transactionSecurityRatings) {
        const filteredTransactionSecurityRatings =
          transactionSecurityRatings.filter(
            (transSecurityRating) =>
              transSecurityRating.transactionSecurityID ===
              item.transactionSecurityID
          )
        filteredTransactionSecurityRatings.forEach((filteredSecurityRating) => {
          if (
            filteredSecurityRating.ratingTypeDescription === 'Long Term Rating'
          ) {
            longTermSecurityRating = filteredSecurityRating.ratingCode
            longTermSecurityRatingAlertCode =
              filteredSecurityRating.ratingAlertCode
          } else if (
            filteredSecurityRating.ratingTypeDescription === 'Short Term Rating'
          ) {
            shortTermSecurityRating = filteredSecurityRating.ratingCode
            shortTermSecurityRatingAlertCode =
              filteredSecurityRating.ratingAlertCode
          }
        })
      }

      return {
        ...item,
        analysts,
        country,
        identifiers,
        endorsements,
        language,
        manager,
        sectors,
        longTermSecurityRating,
        longTermSecurityRatingAlertCode,
        shortTermSecurityRating,
        shortTermSecurityRatingAlertCode
      }
    })
  }

  handleFilterClick = (selectedFilters: string[]) => {
    const { originalData } = this.state

    const result =
      selectedFilters.length > 0
        ? originalData.filter((data: any) =>
            data.ratableRatings.some(
              (rating: RatablesRatingItemProps) =>
                selectedFilters.includes(rating.filterRatingType) ||
                selectedFilters.includes(rating.filterInvestmentGradeType) ||
                selectedFilters.includes(rating.filterNationalRatingType)
            )
          )
        : originalData

    this.setState({ data: result })
  }

  handleRatingHistoryClick = (rowIndex: number, ratableId: string) => {
    const { expanded } = this.state
    const { handleURLParams } = this.props
    const newExpanded: any[] = []
    for (let i = 0; i <= rowIndex; i += 1) {
      if (i === rowIndex) newExpanded[i] = true
      if (expanded[i]) newExpanded[i] = false
    }
    this.setState((prevState) => ({
      isOpen: !prevState.isOpen,
      expanded: newExpanded,
      launchRatingHistoryPopup: !prevState.launchRatingHistoryPopup
    }))
    handleURLParams(ratableId)
  }

  handleSortClick = (sortType: string) => {
    if (sortType === RatablesSortByTypes.MOST_RECENT) {
      this.setState({ sorted: [{ id: ColumnIDs.RATINGS, desc: true }] })
    } else if (sortType === RatablesSortByTypes.DEBT_TYPE) {
      this.setState({ sorted: [{ id: ColumnIDs.IDENTIFIERS, desc: false }] })
    }
  }

  handleRatingsClick = () => {
    this.setState((prevState) => ({
      displayLocalValues: !prevState.displayLocalValues
    }))
  }

  createColumns(displayLocalValues: boolean, isUspf?: boolean) {
    const { isOpen } = this.state
    const ratedEntity = {
      id: ColumnIDs.RATED_ENTITY,
      sortable: true,
      accessor: (data: any) => {
        if (data.security && data.issuer && data.bond) {
          return data.security.concat(data.issuer).concat(data.bond)
        }
        return null
      },
      Header: () => <HeaderCell heading="Rated Entity / Debt" />,
      filterMethod: (filterItem: any, data: any) =>
        ratedEntityFilter(filterItem, data),
      Cell: (cellProps: any) => (
        <RatedEntityCell isUspf={isUspf} key={uniqid()} cellProps={cellProps} />
      )
    }

    const ratings = {
      id: ColumnIDs.RATINGS,
      sortable: true,
      accessor: (data: any) =>
        data.ratableRatings && data.ratableRatings.length
          ? data.ratableRatings[0].ratingEffectiveDate
          : null,
      Header: () => <HeaderCell heading="Ratings" />,
      filterMethod: (filterItem: any, data: any) =>
        ratingsFilter(filterItem, data),
      Cell: (cellProps: any) => (
        <RatingsCell
          isOpen={isOpen}
          cellProps={cellProps}
          displayLocalValues={displayLocalValues}
          handleClick={this.handleRatingHistoryClick}
          key={uniqid()}
          isActive
        />
      )
    }

    const entityDetails = {
      id: ColumnIDs.ENTITY_DETAILS,
      Header: () => <HeaderCell heading="Entity Details" />,
      filterMethod: (filterItem: any, data: any) =>
        entityDetailsFilter(filterItem, data),
      Cell: (cellProps: any) => (
        <EntityDetailsCell
          key={uniqid()}
          cellProps={cellProps}
          isUspf={isUspf}
        />
      )
    }

    let identifiersHeading = 'Debt Type & Identifiers'
    if (isUspf) {
      identifiersHeading = 'Identifiers'
    }
    const identifiers = {
      id: ColumnIDs.IDENTIFIERS,
      sortable: true,
      accessor: (data: any) => {
        if (data.subgroupName && data.ratableTypeDescription) {
          return data.subgroupName.concat(data.ratableTypeDescription)
        }
        if (data.subgroupName) {
          return data.subgroupName
        }
        return null
      },
      Header: () => <HeaderCell heading={identifiersHeading} />,
      filterMethod: (filterItem: any, data: any) =>
        identifiersFilter(filterItem, data),
      Cell: (cellProps: any) => (
        <IdentifiersCell key={uniqid()} cellProps={cellProps} />
      )
    }

    const ratableDetails = {
      id: ColumnIDs.RATABLE_DETAILS,
      Header: () => <HeaderCell heading="Other Details" />,
      filterMethod: (filterItem: any, data: any) =>
        ratableDetailsFilter(filterItem, data),
      Cell: (cellProps: any) => (
        <RatableDetailsCell key={uniqid()} cellProps={cellProps} />
      )
    }

    const hiddenExpander = {
      id: 'hiddenExpander',
      expander: true,
      Header: () => null,
      Cell: () => null,
      Expander: () => null,
      width: 0
    }

    return [
      ratedEntity,
      ratings,
      entityDetails,
      identifiers,
      ratableDetails,
      hiddenExpander
    ]
  }

  render() {
    const {
      data,
      displayLocalValues,
      expanded,
      launchRatingHistoryPopup,
      page,
      pageSize,
      sorted
    } = this.state
    const {
      isTaggedToBrazil,
      isTaggedToLatAm,
      isUspf = false,
      ratablesRatingHistory
    } = this.props
    const ratableIdHash = last(split(window.location.hash, '-'))
    const isPresentRatableId = some(data, ['id', ratableIdHash])
    let ratableRow = isPresentRatableId ? find(data, ['id', ratableIdHash]) : {}
    ratableRow = { original: { ...ratableRow } }
    const columns = this.createColumns(displayLocalValues, isUspf)
    const pageLayout = `page-layout page-layout--2__left-main content`
    return (
      <div className={`${isUspf ? '' : pageLayout}`}>
        {!isUspf ? (
          <div className="column__left" style={{ marginTop: '13px' }}>
            <RatablesFilter handleFilterClick={this.handleFilterClick} />
            <RatablesSort handleClick={this.handleSortClick} />
            <RatablesRatingsFilter
              handleClick={this.handleRatingsClick}
              isTaggedToBrazil={isTaggedToBrazil}
              isTaggedToLatAm={isTaggedToLatAm}
              displayLocalValues={displayLocalValues}
            />
          </div>
        ) : null}
        <div className="column__main">
          <Element name="ratables">
            <ReactTable
              columns={columns}
              data={data}
              defaultPageSize={10}
              expanded={expanded}
              filterable
              key={uniqid()}
              minRows={0}
              page={page}
              pageSize={pageSize}
              sortable
              sorted={sorted}
              onExpandedChange={(newExpanded) =>
                this.setState({ expanded: newExpanded })
              }
              onPageChange={(pageIndex) => {
                this.setState({
                  page: pageIndex
                })
              }}
              onPageSizeChange={(pageSize, pageIndex) => {
                this.setState({
                  page: pageIndex,
                  pageSize
                })
              }}
              onSortedChange={(newSorted) =>
                this.setState({ sorted: newSorted })
              }
            />
          </Element>
        </div>
        {launchRatingHistoryPopup ? (
          <RatingsHistoryPopup
            isUspf={isUspf}
            row={ratableRow}
            ratablesRatingHistory={ratablesRatingHistory}
            handleClick={(rowIndex, ratableId) =>
              this.handleRatingHistoryClick(rowIndex, ratableId)
            }
          />
        ) : null}
      </div>
    )
  }
}

// Ratables.contextType = LocalizationContext
export default Ratables
